import {
  atlassianGatewayBaseUrl,
  atlassianGatewayPattern,
  atlassianServiceAccountApiTokenSteps,
  atlassianServiceAccountOAuth,
  atlassianServiceAccountOAuthSteps,
} from "../atlassianServiceAccount";
import { basicAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const keywords =
  "atlassian jsm jira service desk itsm tickets requests incidents approvals sla customers";

export const jiraServiceManagement = defineProduct({
  name: "Jira Service Management",
  tagLine: "IT service management and help desk",
  logo,
  tags: ["it", "productivity"],
  variants: [
    productVariant({
      identifier: "jiraServiceManagement",
      authLabel: "API token without scopes",
      keywords,
      protocol: "http",
      defaultUrlPattern: () =>
        "https://([^/]*\\.atlassian\\.net/rest/(api|servicedeskapi)|api\\.atlassian\\.com/jsm/assets)/.*",
      auth: (app) =>
        basicAuth({
          username: app.config.email,
          password: app.secret.apiToken,
        }),
      steps: [
        {
          title: "Create an API token",
          items: [
            {
              kind: "markdown",
              content:
                "Sign in as the Atlassian user whose permissions the connector should use, then open [API tokens](https://id.atlassian.com/manage-profile/security/api-tokens). This connection type uses the user’s email address and an API token without scopes. For a service account connection, choose **Service account API token** or **Service account OAuth 2.0**. Give the user only the Jira Service Management permissions needed for the connector’s requests.\n\nClick **Create API token**, then name the token and choose an expiration date.",
            },
          ],
        },
        {
          title: "Copy and paste the API token here",
          items: [
            {
              kind: "input",
              label: "API token",
              required: true,
              target: { kind: "secrets", field: "apiToken" },
            },
          ],
        },
        {
          title: "Enter the account’s email address",
          items: [
            {
              kind: "input",
              label: "API token account email",
              placeholder: "you@example.com",
              required: true,
              target: { kind: "configuration", field: "email" },
            },
          ],
        },
        {
          title: "Enter your Jira Service Management account domain",
          items: [
            {
              kind: "hostname",
              label: "Jira Service Management account domain",
              placeholder: "example.atlassian.net",
              required: true,
              target: { kind: "configuration", field: "domain" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ domain }) =>
          `https://${domain}/rest/servicedeskapi/servicedesk`,
      },
      publicEnvVars: ({ domain, email }) => ({
        JIRA_SERVICE_MANAGEMENT_DOMAIN: domain || "",
        JIRA_SERVICE_MANAGEMENT_EMAIL: email || "",
      }),
    }),
    productVariant({
      identifier: "jiraServiceManagementServiceAccountApiToken",
      authLabel: "Service account API token",
      keywords,
      protocol: "http",
      defaultUrlPattern: ({ cloudId }) =>
        atlassianGatewayPattern("jira", cloudId),
      auth: (app) =>
        basicAuth({
          username: app.config.email,
          password: app.secret.apiToken,
        }),
      steps: atlassianServiceAccountApiTokenSteps(
        "Jira Service Management",
        "read:servicedesk-request"
      ),
      connectionTest: {
        protocol: "http",
        request: ({ cloudId }) =>
          `${atlassianGatewayBaseUrl("jira", cloudId || "cloud-id")}/rest/servicedeskapi/servicedesk`,
      },
      publicEnvVars: ({ cloudId }) => ({
        JIRA_SERVICE_MANAGEMENT_BASE_URL: atlassianGatewayBaseUrl(
          "jira",
          cloudId
        ),
      }),
      usageHint: () =>
        "Use process.env.JIRA_SERVICE_MANAGEMENT_BASE_URL as the Atlassian gateway base URL for Jira Service Management API requests.",
    }),
    productVariant({
      identifier: "jiraServiceManagementServiceAccountOAuth",
      authLabel: "Service account OAuth 2.0",
      keywords,
      protocol: "http",
      defaultUrlPattern: ({ cloudId }) =>
        atlassianGatewayPattern("jira", cloudId),
      auth: oauthAuth,
      steps: atlassianServiceAccountOAuthSteps(
        "Jira Service Management",
        "read:servicedesk-request"
      ),
      oauth: atlassianServiceAccountOAuth,
      connectionTest: {
        protocol: "http",
        request: ({ cloudId }) =>
          `${atlassianGatewayBaseUrl("jira", cloudId || "cloud-id")}/rest/servicedeskapi/servicedesk`,
      },
      publicEnvVars: ({ cloudId }) => ({
        JIRA_SERVICE_MANAGEMENT_BASE_URL: atlassianGatewayBaseUrl(
          "jira",
          cloudId
        ),
      }),
      usageHint: () =>
        "Use process.env.JIRA_SERVICE_MANAGEMENT_BASE_URL as the Atlassian gateway base URL for Jira Service Management API requests.",
    }),
  ],
});
